import React, { useState, useEffect, useRef } from 'react';

const MapPinIcon = () => (
  <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const SUBURB_POSTCODES = {
  'townsville': '4810', 'garbutt': '4814', 'annandale': '4814', 'mount low': '4818'
};

const waitForPlacesLibrary = async () => {
  for (let i = 0; i < 150; i++) {
    if (window.google && window.google.maps && window.google.maps.importLibrary) {
      return window.google.maps.importLibrary('places');
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  throw new Error('Google Maps JS API did not load');
};

const cleanAddress = (address) => (address || '').replace(/,\s*Australia$/i, '').trim();

const AddressAutocomplete = ({ value, onChange, onPostcodeChange, placeholder, required = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [unavailable, setUnavailable] = useState(false);

  const inputRef = useRef(null);
  const placesRef = useRef(null);
  const tokenRef = useRef(null);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);
  const callbacksRef = useRef({ onChange, onPostcodeChange });
  callbacksRef.current = { onChange, onPostcodeChange };

  useEffect(() => {
    let cancelled = false;
    waitForPlacesLibrary()
      .then((lib) => { if (!cancelled) placesRef.current = lib; })
      .catch(() => { if (!cancelled) setUnavailable(true); });
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
      clearTimeout(blurTimerRef.current);
    };
  }, []);

  const fetchSuggestions = async (input) => {
    const lib = placesRef.current;
    if (!lib || !input || input.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    try {
      if (!tokenRef.current) tokenRef.current = new lib.AutocompleteSessionToken();
      const { suggestions: results } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['au'],
        sessionToken: tokenRef.current
      });
      const predictions = (results || []).map(s => s.placePrediction).filter(Boolean);
      setSuggestions(predictions);
      setHighlight(-1);
      setOpen(predictions.length > 0);
    } catch (error) {
      setSuggestions([]);
      setOpen(false);
      setUnavailable(true);
    }
  };

  const applyManualPostcode = (address) => {
    if (!address) return;
    const lower = address.toLowerCase();
    const match = Object.entries(SUBURB_POSTCODES).find(([suburb]) => lower.includes(suburb));
    if (!match) return;
    const [, postcode] = match;
    if (!address.includes(postcode)) {
      callbacksRef.current.onChange?.(`${address} ${postcode}`);
    }
    callbacksRef.current.onPostcodeChange?.(postcode);
  };

  const selectSuggestion = async (prediction) => {
    if (!prediction) return;
    clearTimeout(debounceRef.current);
    setOpen(false);
    setSuggestions([]);
    const displayText = cleanAddress(prediction.text?.toString?.() || '');
    if (displayText) callbacksRef.current.onChange?.(displayText);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress', 'addressComponents'] });
      const full = cleanAddress(place.formattedAddress);
      if (full) callbacksRef.current.onChange?.(full);
      const postcode = (place.addressComponents || []).find(c => c.types.includes('postal_code'));
      if (postcode) callbacksRef.current.onPostcodeChange?.(postcode.longText);
      else applyManualPostcode(full || displayText);
    } catch (error) {
      applyManualPostcode(displayText);
    }
    tokenRef.current = null;
    inputRef.current?.focus();
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    callbacksRef.current.onChange?.(newValue);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(newValue), 250);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === 'Enter' && open) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectSuggestion(suggestions[highlight >= 0 ? highlight : 0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
    if (value && !/\b\d{4}\b/.test(value)) applyManualPostcode(value);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onBlur={handleBlur}
        placeholder={placeholder || 'Start typing address...'}
        required={required}
        autoComplete="off"
        data-testid="address-autocomplete-input"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
      />
      {open && suggestions.length > 0 && (
        <ul
          data-testid="address-suggestions"
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-64 overflow-y-auto py-1"
        >
          {suggestions.map((prediction, index) => {
            const main = prediction.mainText?.toString?.() || prediction.text?.toString?.() || '';
            const secondary = prediction.secondaryText?.toString?.() || '';
            return (
              <li key={prediction.placeId || index}>
                <button
                  type="button"
                  data-testid={`address-suggestion-${index}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => selectSuggestion(prediction)}
                  className={`w-full text-left px-3 py-2 flex items-start gap-2 transition-colors ${
                    highlight === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <MapPinIcon />
                  <span className="text-sm text-gray-900">
                    {main}
                    {secondary && <span className="text-gray-500"> {secondary}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {unavailable && (
        <p data-testid="address-autocomplete-unavailable" className="text-xs text-amber-600 mt-1">
          Address suggestions unavailable — please type the full address including postcode.
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
